import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { XMarkIcon, CalculatorIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { create, all } from "mathjs";

const ANGLE_MODES = ["DEG", "RAD"];

function createMathInstance(angleMode) {
  const math = create(all);

  if (angleMode === "DEG") {
    const replacements = {};
    const fns = ["sin", "cos", "tan", "asin", "acos", "atan"];
    fns.forEach((name) => {
      const orig = math[name];
      if (["sin", "cos", "tan"].includes(name)) {
        replacements[name] = (x) => orig((x * Math.PI) / 180);
      } else {
        replacements[name] = (x) => (orig(x) * 180) / Math.PI;
      }
    });
    math.import(replacements, { override: true });
  }

  return math;
}

const BUTTONS = [
  [
    { label: "sin", type: "func" },
    { label: "cos", type: "func" },
    { label: "tan", type: "func" },
    { label: "π", type: "const", value: "pi" },
    { label: "e", type: "const" },
  ],
  [
    { label: "asin", type: "func" },
    { label: "acos", type: "func" },
    { label: "atan", type: "func" },
    { label: "ln", type: "func", value: "log(" },
    { label: "log₁₀", type: "func", value: "log10(" },
  ],
  [
    { label: "√", type: "func", value: "sqrt(" },
    { label: "x²", type: "op", value: "^2" },
    { label: "xⁿ", type: "op", value: "^" },
    { label: "n!", type: "op", value: "!" },
    { label: "eˣ", type: "func", value: "exp(" },
  ],
  [
    { label: "(", type: "paren" },
    { label: ")", type: "paren" },
    { label: "%", type: "op" },
    { label: "C", type: "action" },
    { label: "⌫", type: "action" },
  ],
  [
    { label: "7", type: "num" },
    { label: "8", type: "num" },
    { label: "9", type: "num" },
    { label: "÷", type: "op", value: "/" },
    { label: "×", type: "op", value: "*" },
  ],
  [
    { label: "4", type: "num" },
    { label: "5", type: "num" },
    { label: "6", type: "num" },
    { label: "-", type: "op" },
    { label: "+", type: "op" },
  ],
  [
    { label: "1", type: "num" },
    { label: "2", type: "num" },
    { label: "3", type: "num" },
    { label: ".", type: "num" },
    { label: "=", type: "equals" },
  ],
  [
    { label: "0", type: "num", span: 2 },
    { label: "±", type: "action" },
    { label: "Ans", type: "action" },
  ],
];

export default function Calculator({ onOpenChange }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [expression, setExpression] = useState("");
  const [display, setDisplay] = useState("0");
  const [lastAnswer, setLastAnswer] = useState("0");
  const [angleMode, setAngleMode] = useState("DEG");
  const [justEvaluated, setJustEvaluated] = useState(false);

  const math = useMemo(() => createMathInstance(angleMode), [angleMode]);

  const panelRef = useRef(null);
  const toggleRef = useRef(null);
  const wasOpenRef = useRef(false);

  // Opening swaps the focused toggle button for the panel, which drops focus to
  // <body>, where digits typed straight away would answer the question instead.
  // Closing swaps them back, and focus returns to the toggle instead of <body>.
  useEffect(() => {
    if (isOpen) panelRef.current?.focus();
    else if (wasOpenRef.current) toggleRef.current?.focus();
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // The exam page lets the question scroll clear of the open panel.
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const handleButton = useCallback(
    (btn) => {
      const { label, type, value } = btn;
      const insert = value || label;

      switch (type) {
        case "num": {
          if (justEvaluated) {
            setExpression(label);
            setDisplay(label);
            setJustEvaluated(false);
          } else {
            setExpression((prev) => prev + label);
            setDisplay((prev) => (prev === "0" && label !== "." ? label : prev + label));
          }
          break;
        }

        case "op":
          setExpression((prev) => prev + insert);
          setDisplay("0");
          setJustEvaluated(false);
          break;

        case "func":
          if (justEvaluated) {
            setExpression(insert);
          } else {
            setExpression((prev) => prev + (value || `${label}(`));
          }
          setDisplay("0");
          setJustEvaluated(false);
          break;

        case "const":
          if (justEvaluated) {
            setExpression(insert);
          } else {
            setExpression((prev) => prev + insert);
          }
          setDisplay(label);
          setJustEvaluated(false);
          break;

        case "paren":
          setExpression((prev) => prev + label);
          setJustEvaluated(false);
          break;

        case "action":
          if (label === "C") {
            setDisplay("0");
            setExpression("");
            setJustEvaluated(false);
          } else if (label === "⌫") {
            setExpression((prev) => prev.slice(0, -1));
            setDisplay((prev) => prev.slice(0, -1) || "0");
            setJustEvaluated(false);
          } else if (label === "±") {
            setExpression((prev) =>
              prev.startsWith("-(") ? prev.slice(2, -1) : `-(${prev})`
            );
            setDisplay((prev) =>
              prev.startsWith("-") ? prev.slice(1) : "-" + prev
            );
          } else if (label === "Ans") {
            setExpression((prev) => prev + lastAnswer);
            setDisplay(lastAnswer);
            setJustEvaluated(false);
          }
          break;

        case "equals": {
          try {
            const result = math.evaluate(expression);
            const formatted =
              typeof result === "number"
                ? parseFloat(result.toPrecision(12)).toString()
                : String(result);
            setDisplay(formatted);
            setExpression(formatted);
            setLastAnswer(formatted);
          } catch {
            setDisplay(t("exam_taking.calculator.error"));
            setExpression("");
          }
          setJustEvaluated(true);
          break;
        }

        default:
          break;
      }
    },
    [expression, justEvaluated, lastAnswer, math, t]
  );

  const getButtonStyle = (btn) => {
    const base =
      "flex items-center justify-center rounded-lg text-sm font-medium transition duration-150 select-none active:scale-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600 ";
    switch (btn.type) {
      case "equals":
        return base + "bg-blue-600 text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700";
      case "action":
        return btn.label === "C"
          ? base + "bg-red-50 text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-100"
          : base + "bg-slate-200 text-slate-700 hover:bg-slate-300";
      case "op":
        return base + "bg-blue-50 text-blue-700 hover:bg-blue-100";
      case "func":
      case "const":
        return base + "bg-slate-100 text-slate-700 hover:bg-slate-200";
      default:
        return base + "bg-white text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 hover:bg-slate-50";
    }
  };

  if (!isOpen) {
    return (
      <button
        ref={toggleRef}
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-5 right-5 z-40 rounded-full bg-blue-600 p-3.5 text-white shadow-lg shadow-blue-600/30 ring-4 ring-white transition duration-200 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        title={t("exam_taking.calculator.title")}
        aria-label={t("exam_taking.calculator.title")}
      >
        <CalculatorIcon className="h-6 w-6" />
      </button>
    );
  }

  // ExamSession's shortcuts ignore keys typed in here. tabIndex lets the panel
  // hold focus on Safari and macOS Firefox, which don't focus clicked buttons.
  return (
    <div ref={panelRef} data-exam-calculator tabIndex={-1} className="fixed bottom-5 right-5 z-40 w-80 max-w-[calc(100vw-2.5rem)] overflow-hidden rounded-2xl bg-slate-50 shadow-2xl ring-1 ring-slate-900/10">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2 text-slate-700">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <CalculatorIcon className="h-4 w-4" />
          </span>
          <span className="text-sm font-semibold text-slate-900">{t("exam_taking.calculator.title")}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() =>
              setAngleMode((prev) =>
                ANGLE_MODES[(ANGLE_MODES.indexOf(prev) + 1) % ANGLE_MODES.length]
              )
            }
            className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 transition duration-200 hover:bg-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            {angleMode}
          </button>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label={t("buttons.close")}
            className="rounded-lg p-1.5 text-slate-400 transition duration-200 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Display */}
      <div className="mx-2 mt-2 rounded-xl bg-slate-900 px-3 py-2.5 text-right">
        <div className="h-5 truncate font-mono text-xs text-slate-400">{expression || "\u00A0"}</div>
        <div className="truncate font-mono text-2xl tabular-nums text-white">{display}</div>
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-1.5 p-2">
        {BUTTONS.map((row, ri) => (
          <div key={ri} className="grid grid-cols-5 gap-1.5">
            {row.map((btn, bi) => (
              <button
                key={bi}
                type="button"
                onClick={() => handleButton(btn)}
                className={`${getButtonStyle(btn)} h-9`}
                style={btn.span ? { gridColumn: `span ${btn.span}` } : undefined}
              >
                {btn.label}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
